import React, { useState, useEffect } from 'react';
import ContentHeader from '../components/ContentHeader';
import { supportAgentAPI } from '../services/api';

/**
 * Super Admin → Releases: app versions that support cases can be marked "fixed in".
 */
export function SuperAdminReleasesTab() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ version: '', released_at: '', git_ref: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await supportAgentAPI.listReleases();
      setReleases(res.data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleCreate = async () => {
    if (!form.version.trim()) { setError('Version is required'); return; }
    try {
      setSaving(true);
      setError(null);
      await supportAgentAPI.createRelease({
        version: form.version.trim(),
        released_at: form.released_at || null,
        git_ref: form.git_ref || null,
        notes: form.notes || null,
      });
      setForm({ version: '', released_at: '', git_ref: '', notes: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create release');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—');

  return (
    <div className="super-admin-tab-content">
      {error && <div className="alert alert-error"><strong>Error:</strong> {error}</div>}
      <ContentHeader
        title="Releases"
        titleHint="App versions that cases can be marked 'fixed in'. Add a release when you ship."
      />
      <div className="super-admin-actions super-admin-actions--full" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input name="version" placeholder="Version (e.g. v1.4.0)" value={form.version} onChange={handleChange} disabled={saving} />
          <input name="released_at" type="date" value={form.released_at} onChange={handleChange} disabled={saving} title="Released date" />
          <input name="git_ref" placeholder="Git tag/sha (optional)" value={form.git_ref} onChange={handleChange} disabled={saving} />
          <input name="notes" placeholder="Notes (optional)" value={form.notes} onChange={handleChange} disabled={saving} style={{ minWidth: '220px' }} />
          <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? 'Saving…' : 'Add release'}
          </button>
        </div>
      </div>
      <div className="super-admin-table">
        <table>
          <thead>
            <tr>
              <th>Version</th>
              <th>Released</th>
              <th>Git ref</th>
              <th>Cases fixed</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="no-data">Loading…</td></tr>
            ) : releases.length === 0 ? (
              <tr><td colSpan="5" className="no-data">No releases yet.</td></tr>
            ) : (
              releases.map((r) => (
                <tr key={r.id}>
                  <td>{r.version}</td>
                  <td>{fmt(r.released_at)}</td>
                  <td>{r.git_ref || '—'}</td>
                  <td>{r.case_count ?? 0}</td>
                  <td>{r.notes || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SuperAdminReleasesTab;
